/**
 * PIN Setup — US-E1-003 part 3, redesign 2026-05-09 (banking pattern).
 *
 * 2-step wizard:
 *   Step 1 «set»     — заполнил 4 цифры → переход на confirm
 *   Step 2 «confirm» — заполнил 4 цифры → match? save : shake + back to step 1
 *
 * Mobile/tablet only (Phase 1 mobile gate). На desktop — friendly redirect.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { authApi } from '@/api/auth'
import { ApiError } from '@/api/client'
import { useAuth } from '@/stores/auth'
import { getDeviceId, getDeviceLabel } from '@/lib/deviceId'
import { useIsPinCapable } from '@/lib/device'
import { Icon } from '@/components/icons/Icon'
import { AuthLayout } from '@/components/auth/AuthLayout'
import { PinDots } from '@/components/auth/PinDots'
import { NumPad } from '@/components/auth/NumPad'
import { ErrorLayout, ErrorBlock } from '@/components/layout/ErrorLayout'
import { Button as UIButton } from '@/components/ui/Button'
import { toast } from '@/components/ui/toast'

const PIN_LENGTH = 4

type Step = 'set' | 'confirm' | 'submitting' | 'success'

export function PinSetup() {
  const { t } = useTranslation()
  const isAuthed = useAuth((s) => s.isAuthed)
  const isPinCapable = useIsPinCapable()

  // Phase 1 mobile gate
  if (!isPinCapable) {
    return (
      <ErrorLayout>
        <ErrorBlock
          icon="smartphone"
          iconTone="brand"
          title={t('auth.pin_setup.desktop_title', 'PIN — для мобильного устройства')}
          body={
            <p>
              {t(
                'auth.pin_setup.desktop_body',
                'PIN-код доступен на телефоне или планшете. На компьютере используй пароль или OAuth-вход.',
              )}
            </p>
          }
          actions={
            <>
              <UIButton asChild variant="primary">
                <a href="/">{t('common.home', 'На главную')}</a>
              </UIButton>
              <UIButton asChild variant="ghost">
                <a href="/app/settings/security">
                  {t('auth.pin_setup.open_security', 'Настройки безопасности')}
                </a>
              </UIButton>
            </>
          }
        />
      </ErrorLayout>
    )
  }

  const [step, setStep] = useState<Step>('set')
  const [firstPin, setFirstPin] = useState('')
  const [pin, setPin] = useState('')
  const [errorState, setErrorState] = useState(false)
  const [successFlash, setSuccessFlash] = useState(false)

  const m = useMutation({
    mutationFn: (body: { pin: string; device_id: string; device_label: string }) =>
      authApi.pinSetup(body),
    onSuccess: () => {
      // Step 3 success screen вместо toast+timeout. Юзер сам решает когда
      // переходить через кнопку «Продолжить» — banking-pattern (как в Avast).
      setSuccessFlash(true)
      setTimeout(() => {
        setStep('success')
      }, 350)
    },
    onError: (e: ApiError) => {
      setErrorState(true)
      setTimeout(() => {
        setStep('set')
        setFirstPin('')
        setPin('')
        setErrorState(false)
      }, 400)

      if (e.status === 401) {
        toast.error(
          t('auth.pin_setup.unauthorized_title', 'Требуется вход'),
          t('auth.pin_setup.unauthorized_body', 'Войди по паролю и попробуй ещё раз.'),
        )
        setTimeout(() => {
          window.location.href = '/auth/login'
        }, 1200)
      } else {
        toast.error(
          t('auth.pin_setup.network_title', 'Не получилось'),
          t('auth.pin_setup.network_body', 'Не удалось сохранить PIN. Попробуй ещё раз.'),
        )
      }
    },
  })

  // Step 1 → 2: при заполнении первого PIN'а — green flash, потом переход
  useEffect(() => {
    if (step === 'set' && pin.length === PIN_LENGTH) {
      setSuccessFlash(true)
      setTimeout(() => {
        setSuccessFlash(false)
        setFirstPin(pin)
        setPin('')
        setStep('confirm')
      }, 250)
    }
  }, [pin, step])

  // Step 2 → submit: при заполнении confirm проверяем совпадение
  useEffect(() => {
    if (step !== 'confirm' || pin.length !== PIN_LENGTH || m.isPending || errorState || successFlash) return

    if (!isAuthed) {
      toast.warning(t('auth.pin_setup.need_login', 'Сначала войди в аккаунт'))
      // Минор-fix: сбросить pin чтобы юзер не «застрял» с 4 заполненными dots
      setTimeout(() => setPin(''), 200)
      return
    }

    if (pin !== firstPin) {
      // Mismatch — shake → reset на step 1
      setErrorState(true)
      setTimeout(() => {
        setStep('set')
        setFirstPin('')
        setPin('')
        setErrorState(false)
        toast.warning(t('auth.pin_setup.mismatch', 'Не совпало. Попробуй ещё раз'))
      }, 400)
      return
    }

    setStep('submitting')
    m.mutate({ pin, device_id: getDeviceId(), device_label: getDeviceLabel() })
  }, [pin, step, firstPin, m, isAuthed, errorState, successFlash, t])

  const inputDisabled =
    m.isPending || errorState || successFlash || step === 'submitting' || step === 'success'

  // Reset на Step 1 — кнопка «← Изменить PIN» в header step 2.
  // Чистит весь pin-state, возвращает юзера в начало wizard.
  const resetToStep1 = () => {
    setStep('set')
    setFirstPin('')
    setPin('')
    setErrorState(false)
    setSuccessFlash(false)
  }

  const onDigit = (d: string) => {
    if (inputDisabled) return
    setPin((p) => (p.length >= PIN_LENGTH ? p : p + d))
  }
  const onDelete = () => {
    if (inputDisabled) return
    setPin((p) => p.slice(0, -1))
  }

  // ─── Step 3: success screen (banking-pattern, как 3-й экран Avast) ─────────
  if (step === 'success') {
    return (
      <AuthLayout side="login">
        <div className="text-center space-y-6 max-w-sm mx-auto">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-status-success-bg text-status-success-text mx-auto">
            <Icon name="check-circle" size={40} />
          </div>

          <div className="space-y-2">
            <h1 className="text-3xl font-bold font-display text-text-primary">
              {t('auth.pin_setup.success_title', 'PIN сохранён')}
            </h1>
            <p className="text-sm text-text-secondary">
              {t(
                'auth.pin_setup.success_body',
                'Теперь сможешь быстро входить с этого устройства. На других — нужен полный пароль.',
              )}
            </p>
          </div>

          <div className="rounded-lg bg-surface-subtle border border-border p-4 text-left">
            <p className="text-xs text-text-muted mb-1">
              {t('auth.pin_setup.success_device', 'Устройство:')}
            </p>
            <p className="text-sm font-semibold text-text-primary">{getDeviceLabel()}</p>
          </div>

          <UIButton
            variant="primary"
            size="lg"
            fullWidth
            onClick={() => {
              window.location.href = '/'
            }}
          >
            {t('auth.pin_setup.success_cta', 'Продолжить')}
          </UIButton>

          <p className="text-xs text-text-muted">
            {t('auth.pin_setup.success_change_hint', 'PIN можно поменять или удалить в')}{' '}
            <a href="/app/settings/security" className="text-text-link font-semibold">
              {t('auth.pin_setup.success_security_link', 'Настройки → Безопасность')}
            </a>
          </p>
        </div>
      </AuthLayout>
    )
  }

  // ─── Step 1 (set) и Step 2 (confirm) — общий layout с NumPad ───────────────
  const heading =
    step === 'set'
      ? t('auth.pin_setup.title_set', 'Создай PIN-код')
      : t('auth.pin_setup.title_confirm', 'Повтори PIN')

  const subtitle =
    step === 'set'
      ? t('auth.pin_setup.sub_set', '4 цифры для быстрого входа на этом устройстве')
      : t('auth.pin_setup.sub_confirm', 'Введи те же цифры ещё раз')

  return (
    <AuthLayout side="login">
      {step === 'set' ? (
        <a
          href="/"
          className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary text-sm font-medium mb-8"
        >
          <Icon name="arrow-left" size={16} />
          {t('common.back', 'Назад')}
        </a>
      ) : (
        // Step 2 (confirm): «← Изменить PIN» — возврат на step 1 без выхода
        // из wizard. Принцип обратимости (iOS Lock / Avast pattern).
        <button
          type="button"
          onClick={resetToStep1}
          disabled={inputDisabled}
          className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary text-sm font-medium mb-8 disabled:opacity-40"
        >
          <Icon name="arrow-left" size={16} />
          {t('auth.pin_setup.back_to_step1', 'Изменить PIN')}
        </button>
      )}

      <div className="text-center space-y-2 mb-8">
        <h1 className="text-3xl font-bold font-display text-text-primary">{heading}</h1>
        <p className="text-sm text-text-secondary">{subtitle}</p>
        <p className="text-xs text-text-muted">
          {t('auth.pin_setup.step_label', 'Шаг')} {step === 'set' ? '1' : '2'}{' '}
          {t('auth.pin_setup.of', 'из')} 2
        </p>
      </div>

      <div className="my-10">
        <PinDots
          length={PIN_LENGTH}
          filled={pin.length}
          error={errorState}
          success={successFlash}
        />
      </div>

      <NumPad onDigit={onDigit} onDelete={onDelete} disabled={inputDisabled} />

      <p className="text-center text-xs text-text-muted mt-8">
        {t('auth.pin_setup.device_hint', 'Устройство:')}{' '}
        <span className="font-semibold">{getDeviceLabel()}</span>
      </p>

      <p className="text-center text-sm text-text-secondary mt-4">
        <a
          href="/"
          className="inline-flex items-center min-h-[24px] px-1 text-text-link font-semibold"
        >
          {t('auth.pin_setup.skip', 'Пропустить — войду паролем')}
        </a>
      </p>
    </AuthLayout>
  )
}
